import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Loader2, Droplets } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES = [
  { id: "interior", label: "Interior Detail · $120" },
  { id: "exterior", label: "Exterior Detail · $100" },
  { id: "premium", label: "Premium Full Detail · $250" },
];

const TIMES = [
  "8:00 AM",
  "10:00 AM",
  "12:00 PM",
  "2:00 PM",
  "4:00 PM",
];

const today = () => new Date().toISOString().split("T")[0];

const empty = {
  name: "",
  email: "",
  phone: "",
  address: "",
  vehicle: "",
  package: "premium",
  preferred_date: "",
  preferred_time: "",
  notes: "",
};

export default function Booking() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onSet = (e) => {
      const pkg = e.detail;
      if (PACKAGES.find((p) => p.id === pkg)) {
        setForm((f) => ({ ...f, package: pkg }));
      }
    };
    window.addEventListener("booking:set-package", onSet);
    return () => window.removeEventListener("booking:set-package", onSet);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const required = ["name", "email", "phone", "address", "vehicle", "preferred_date", "preferred_time"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      toast.success(
        `Booking request received! We'll contact you shortly.`,
        { description: `Confirmation #${res.data.id.slice(0, 8).toUpperCase()}` }
      );
      setForm(empty);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#161616] border border-white/10 px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] focus:outline-none transition-colors";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 lg:py-32 bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="divider-red" />
          <span className="section-label">Book a Detail</span>
        </div>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-8 max-w-3xl">
          Tell us about your car. <br />
          We&apos;ll handle the <span className="text-[#E63946]">shine</span>.
        </h2>

        <div
          data-testid="booking-disclaimer"
          className="mb-12 flex items-start gap-4 border-l-2 border-[#E63946] bg-[#141414] px-5 py-4 max-w-3xl"
        >
          <Droplets className="w-5 h-5 text-[#E63946] mt-0.5 shrink-0" />
          <div>
            <div className="section-label text-[#E63946]">Please Note</div>
            <p className="mt-1 text-sm text-zinc-300 leading-relaxed">
              As a mobile service, customers must provide access to a working <span className="text-white font-semibold">water spigot</span> and an <span className="text-white font-semibold">electrical outlet</span> at the service location. Without both, we&apos;re unable to complete the detail.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <aside className="lg:col-span-4">
            <div className="border border-white/10 bg-[#121212] p-7">
              <h3 className="font-display text-xl font-semibold tracking-tight">
                Direct Contact
              </h3>
              <p className="mt-2 text-sm text-zinc-400">
                Prefer to talk first? Reach out and we&apos;ll answer questions or quote your
                vehicle on the spot.
              </p>

              <div className="mt-6 space-y-5">
                <a
                  data-testid="contact-phone"
                  href="tel:8134818875"
                  className="group flex items-start gap-4 hover:text-white"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <Phone className="w-4 h-4 text-[#E63946]" />
                  </span>
                  <div>
                    <div className="section-label">Phone</div>
                    <div className="text-white font-medium mt-1">813-481-8875</div>
                  </div>
                </a>

                <a
                  data-testid="contact-email"
                  href="mailto:barnettautodetailingllc@gmail.com"
                  className="group flex items-start gap-4 hover:text-white break-all"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <Mail className="w-4 h-4 text-[#E63946]" />
                  </span>
                  <div>
                    <div className="section-label">Email</div>
                    <div className="text-white font-medium mt-1">
                      barnettautodetailingllc@gmail.com
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-location">
                  <span className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <MapPin className="w-4 h-4 text-[#E63946]" />
                  </span>
                  <div>
                    <div className="section-label">Service Area</div>
                    <div className="text-white font-medium mt-1">
                      Tampa, FL & Surrounding
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="section-label">Hours</div>
                <div className="mt-2 text-sm text-zinc-300 space-y-1">
                  <div className="flex justify-between"><span>Mon – Sat</span><span>9 AM – 5 PM</span></div>
                  <div className="flex justify-between"><span>Sunday</span><span>Closed</span></div>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="lg:col-span-8 border border-white/10 bg-[#121212] p-7 lg:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="section-label">Full Name *</label>
                <input
                  data-testid="booking-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jordan Barnett"
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div>
                <label className="section-label">Phone *</label>
                <input
                  data-testid="booking-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(813) 555-0000"
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="section-label">Email *</label>
                <input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@email.com"
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="section-label">Service Address *</label>
                <input
                  data-testid="booking-address"
                  type="text"
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Street, City, ZIP"
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div>
                <label className="section-label">Vehicle (Make & Model) *</label>
                <input
                  data-testid="booking-vehicle"
                  type="text"
                  value={form.vehicle}
                  onChange={set("vehicle")}
                  placeholder="2021 Tesla Model 3"
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div>
                <label className="section-label">Package *</label>
                <select
                  data-testid="booking-package"
                  value={form.package}
                  onChange={set("package")}
                  className={`mt-2 ${inputCls}`}
                >
                  {PACKAGES.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#161616]">
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="section-label">Preferred Date *</label>
                <input
                  data-testid="booking-date"
                  type="date"
                  min={today()}
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                  className={`mt-2 ${inputCls}`}
                  required
                />
              </div>
              <div>
                <label className="section-label">Preferred Time *</label>
                <select
                  data-testid="booking-time"
                  value={form.preferred_time}
                  onChange={set("preferred_time")}
                  className={`mt-2 ${inputCls}`}
                  required
                >
                  <option value="" className="bg-[#161616]">Select a time</option>
                  {TIMES.map((t) => (
                    <option key={t} value={t} className="bg-[#161616]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="section-label">Notes (optional)</label>
                <textarea
                  data-testid="booking-notes"
                  value={form.notes}
                  onChange={set("notes")}
                  rows={4}
                  placeholder="Pet hair, heavy soil, paint concerns, gate code, etc."
                  className={`mt-2 ${inputCls} resize-none`}
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-zinc-500 max-w-md">
                By submitting, you&apos;ll receive a confirmation call or text from us at
                813-481-8875 to confirm timing &amp; pricing for your vehicle.
              </p>
              <button
                data-testid="booking-submit"
                type="submit"
                disabled={submitting}
                className="cta-glow inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#F0545E] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 font-semibold tracking-wide"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Request Booking <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
